<script lang="ts">
import { ComplaintAdditionalDoc, ComplaintInfo } from '@/interface/Complaint.interface';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { ascRequestBreadcrumb } from '@/components/utils';
import Breadcrumb from '@/views/complaint/breadcrumb.vue';
import RequestHeader from '@/components/pages/complaint/detail/header.vue';
import RequestInformation from '@/components/pages/complaint/detail/information.vue';
import RequestHistory from './history.vue';
import RequestAdditionalDoc from '../shared/reqDoc.vue';
import AssignDepartment from './assignDpm.vue';
import VerifyAdditionalDoc from '../shared/viewDoc.vue';
import CancelComplaint from './cancel.vue';
import RequestAttachment from '../shared/attachment.vue';
import { BaseConfirm } from '@/interface/BaseConfirm.interface';
import { BaseAlert } from '@/interface/BaseAlert.interface';
import { ADD_DOCS_STATUS, ASC_BTN_KEY, DPM_STATUS, LANG_EN, OPINION, REQUEST_STATUS } from '@/app/const';
import { complaintService, fileService, masterService, userService } from '@/services/serviceProvider';
import { groupBy } from '@/app/common/types/groupBy.type';
import AllAnswer from './allAnswer.vue';
import { UserEndReason } from '@/interface/UserEndReason.interface';
import AscAnswer from '../shared/ascAnswer.vue';
import { AttachmentUploaderType, AttachmentUrl } from '@/app/common/types/attachUploader';
import { UploadResponse } from '@/interface/FileUpload.interface';
import RequestAppeal from '@/components/pages/complaint/detail/appeal.vue';

export default {
    components: {
        Breadcrumb,
        RequestHeader,
        RequestInformation,
        RequestHistory,
        RequestAdditionalDoc,
        AssignDepartment,
        VerifyAdditionalDoc,
        CancelComplaint,
        RequestAttachment,
        AllAnswer,
        AscAnswer,
        RequestAppeal
    },
    setup() {
        const router = useRouter();
        const currentRoute = router.currentRoute.value;
        // components
        const flight = ref();
        const passenger = ref();
        const topic = ref();
        // pdf
        const pdfContent = ref();
        // service
        const userSv = userService;

        return { router, currentRoute, flight, passenger, topic, pdfContent, userSv }
    },
    props: {
        modelValue: {
            type: Object,
            default: {},
        },
    },
    emits: [
        'update:modelValue',
        'displayConfirmDialog',
        'displayAlertDialog',
        'exportPdf',
    ],
    data() {
        return {
            model: this.modelValue as ComplaintInfo,
            userEndReasons: [] as UserEndReason[],
            breadcrumb: [...ascRequestBreadcrumb],
            loading: false,
            displayLoading: false as boolean,
            modal: {
                displayHistory: false as boolean,
                displayReqDoc: false as boolean,
                displayAssignDpm: false as boolean,
                displayViewDoc: false as boolean,
                displaySendDocToDpm: false as boolean,
                displayCancelComplaint: false as boolean,
            },
            lang: this.$i18n.locale,
        }
    },
    watch: {
        '$i18n.locale': function (newVal, oldVal) {
            this.lang = newVal;
        }
    },
    computed: {
        isNew() {
            return this.currentRoute.params.requestNo === undefined;
        },
        allAdditionalDocs() {
            const ascDocs = this.model.ascDepartment?.additionalDocs || [];
            const mainDpmDocs = this.model.relatedMainDepartment?.additionalDocs || [];
            let subDpmDocs = [] as ComplaintAdditionalDoc[];
            this.model.relatedSubDepartments?.forEach(o => {
                const docs = o.additionalDocs || [];
                if (docs && docs.length > 0) {
                    subDpmDocs = subDpmDocs.concat(docs);
                }
            })
            return ascDocs.concat(mainDpmDocs).concat(subDpmDocs);
        },
        waitApproveDocs() {
            return this.allAdditionalDocs.filter(o => o.status === ADD_DOCS_STATUS.NEW)
        },
        uploadedDocs() {
            return this.allAdditionalDocs.filter(o => o.status === ADD_DOCS_STATUS.UPLOADED)
        },
        someDpmWaitVerify(): boolean {
            if (this.model.relatedMainDepartment?.departmentStatus === DPM_STATUS.ASC_CONSIDER
                || this.model.relatedSubDepartments?.map(o => o.departmentStatus).includes(DPM_STATUS.ASC_CONSIDER))
                return true;
            return false;
        },
        someDpmWaitSendDoc(): boolean {
            if (this.model.relatedMainDepartment?.departmentStatus === DPM_STATUS.UPLOADED
                || this.model.relatedSubDepartments?.map(o => o.departmentStatus).includes(DPM_STATUS.UPLOADED))
                return true;
            return false;
        },
        mainDpmReturn(): boolean {
            return this.model.relatedMainDepartment?.departmentStatus === DPM_STATUS.RETURN
        },
        allDpmEnd() {
            if (!this.model.relatedMainDepartment)
                return true;

            if (this.model.relatedMainDepartment?.departmentStatus === DPM_STATUS.END
                || this.model.relatedMainDepartment?.departmentStatus === DPM_STATUS.RETURN
            ) {
                const allSubStatuses = this.model.relatedSubDepartments?.map(o => o.departmentStatus);
                let end = 0;
                allSubStatuses?.forEach(status => {
                    if (status === DPM_STATUS.END || status === DPM_STATUS.RETURN) {
                        end++;
                    }
                });
                if (this.model.relatedSubDepartments?.length === end) {
                    return true;
                }
            }
            return false;
        },
        subDpmInprog(): boolean {
            if (this.model.relatedSubDepartments && this.model.relatedSubDepartments.length > 0) {
                let dpms = this.model.relatedSubDepartments?.map(o => o.departmentStatus);
                return dpms.length !== dpms.filter(o => o === DPM_STATUS.END || o === DPM_STATUS.RETURN).length;
            }
            return false;
        },
        filteredBreadcrumb() {
            switch (this.model.status) {
                case REQUEST_STATUS.ACTIVE:
                    let bActive = [...this.breadcrumb];
                    if (this.haveMainDepartment) {
                        bActive = bActive.filter(o => o.key !== ASC_BTN_KEY.ASSIGN_DPM)
                    }
                    if (this.disableAscEdit) {
                        bActive = bActive.filter(o => o.key !== ASC_BTN_KEY.SAVE)
                    }
                    return bActive.filter(o => o.key !== ASC_BTN_KEY.VERIFY_DOC
                        && o.key !== ASC_BTN_KEY.SEND_DOC
                    );
                case REQUEST_STATUS.IN_PROGRESS:
                    return this.getBreadcrumbInprog;
                case REQUEST_STATUS.REQUEST_DOC:
                    return this.breadcrumb.filter(o => o.key === ASC_BTN_KEY.EXPORT
                        || o.key === ASC_BTN_KEY.HISTORY || o.key === ASC_BTN_KEY.END
                        || o.key === ASC_BTN_KEY.SAVE
                    );
                case REQUEST_STATUS.END:
                case REQUEST_STATUS.CANCELLED:
                default:
                    return this.breadcrumb.filter(o => o.key === ASC_BTN_KEY.EXPORT
                        || o.key === ASC_BTN_KEY.HISTORY
                    );
            }
        },
        getBreadcrumbInprog() {
            // Case: ASC มอบหมายให้ฝ่าย ASC
            let ascIsMainDpm = this.model.relatedMainDepartment?.departmentCode === 'ASC';
            let ascIsSubDpm = this.model.relatedSubDepartments?.map(o => o.departmentCode).includes('ASC');
            if (userService.isAsc() && (ascIsMainDpm || ascIsSubDpm)) {
                if (ascIsMainDpm) {
                    let ascStatus = this.model.relatedMainDepartment?.departmentStatus;
                    let resp = this.model.relatedMainDepartment?.responiblePersons?.find(o => o.activeResponsible);
                    if (ascStatus === DPM_STATUS.IN_PROGRESS && userService.currentUserInfo()?.employeeId === resp?.employeeId) {
                        // ให้สามารถ assign department ได้อีกครั้ง เพื่อป้องกันการติด loop
                        return this.breadcrumb.filter(o => o.key === ASC_BTN_KEY.EXPORT
                            || o.key === ASC_BTN_KEY.HISTORY || o.key === ASC_BTN_KEY.SAVE
                            || o.key === ASC_BTN_KEY.ASSIGN_DPM
                        );
                    }
                } else {
                    let ascStatus = this.model.relatedSubDepartments?.find(o => o.departmentCode === 'ASC')?.departmentStatus;
                    let resp = this.model.relatedSubDepartments?.find(o => o.departmentCode === 'ASC')?.responiblePersons?.find(o => o.activeResponsible);
                    if (ascStatus === DPM_STATUS.IN_PROGRESS && userService.currentUserInfo()?.employeeId === resp?.employeeId) {
                        // ให้สามารถ assign department ได้อีกครั้ง เพื่อป้องกันการติด loop
                        return this.breadcrumb.filter(o => o.key === ASC_BTN_KEY.EXPORT
                            || o.key === ASC_BTN_KEY.HISTORY || o.key === ASC_BTN_KEY.SAVE
                            || o.key === ASC_BTN_KEY.ASSIGN_DPM
                        );
                    }
                }
            }
            // Case: Other
            let bInprog = [...this.breadcrumb.filter(o => o.key === ASC_BTN_KEY.EXPORT
                || o.key === ASC_BTN_KEY.HISTORY
            )];
            let assignBtn = this.breadcrumb.find(o => o.key === ASC_BTN_KEY.ASSIGN_DPM);
            let verifyBtn = this.breadcrumb.find(o => o.key === ASC_BTN_KEY.VERIFY_DOC);
            let sendDocBtn = this.breadcrumb.find(o => o.key === ASC_BTN_KEY.SEND_DOC);
            let endDocBtn = this.breadcrumb.find(o => o.key === ASC_BTN_KEY.END);
            let saveDocBtn = this.breadcrumb.find(o => o.key === ASC_BTN_KEY.SAVE);
            if (((this.mainDpmReturn && !this.subDpmInprog) || (this.allDpmEnd && this.mainDpmSelectCoordinate)) && assignBtn) bInprog.push(assignBtn);
            if (this.someDpmWaitVerify && verifyBtn) bInprog.push(verifyBtn);
            if (this.someDpmWaitSendDoc && sendDocBtn) bInprog.push(sendDocBtn);
            if (!this.disableAscEdit && saveDocBtn) bInprog.push(saveDocBtn);
            if (this.allDpmEnd && endDocBtn) bInprog.push(endDocBtn);

            return bInprog;
        },
        haveMainDepartment(): boolean {
            return this.model.relatedMainDepartment !== undefined
                && this.model.relatedMainDepartment.departmentCode !== undefined
                && this.model.relatedMainDepartment.departmentCode !== null;
        },
        requestEnd() {
            return this.model.status === REQUEST_STATUS.END
                || this.model.status === REQUEST_STATUS.APPEAL
        },
        enableAscEndReason(): boolean {
            return this.requestEnd
                && this.model.ascEndReason !== undefined
                && this.model.ascEndReason !== null;
        },
        enableUserEndReason(): boolean {
            return this.requestEnd
                && (this.model.ascEndReason === undefined
                    || this.model.ascEndReason === null);
        },
        disableAscEdit() {
            return this.requestEnd
                || this.model.status === REQUEST_STATUS.CANCELLED;
        },
        mainDpmSelectCoordinate() {
            return this.model.relatedMainDepartment?.staffOpinionCode === OPINION.COORDINATE
        }
    },
    async mounted() {
        // user end reasons
        this.getUserEndReasons();
    },
    methods: {
        async getUserEndReasons() {
            try {
                const res = await masterService.getEndReasons();
                if (res && res.status === 200) {
                    this.userEndReasons = res.data as UserEndReason[];
                }
            } catch (e) {
            }
        },
        async requestAdditionalDocs() {
            try {
                if (this.model.requestNo) {
                    const docs = this.model.ascDepartment?.additionalDocs;
                    const res = await complaintService.requestAdditionalDocs(this.model.requestNo, docs)
                    if (res && res.status === 200) {
                        this.displayAlertDialog({
                            success: true,
                            header: this.$t('t-save-success'),
                            messageTitle: this.$t('t-action-need-more-doc-success'),
                            callBack: this.goToListPage
                        } as BaseAlert)
                    }
                }
            } catch (e) {
                this.displayAlertDialog({
                    display: true,
                    success: false,
                    messageTitle: this.$t('t-action-need-more-doc-fail'),
                } as BaseAlert)
            }
        },
        async assignDepartment(data: any) {
            try {
                if (this.model.requestNo) {
                    const res = await complaintService.assignDepartment(this.model.requestNo, data);
                    if (res && res.status === 200) {
                        this.displayAlertDialog({
                            success: true,
                            header: this.$t('t-save-success'),
                            messageTitle: this.$t('t-action-assign-dpm-success'),
                            callBack: this.goToListPage
                        } as BaseAlert)
                    } else {
                        throw new Error();
                    }
                }
            } catch (e) {
                this.displayAlertDialog({
                    display: true,
                    success: false,
                    messageTitle: this.$t('t-action-assign-dpm-fail'),
                } as BaseAlert)
            }
        },
        async verifyAdditionalDocs(action: string) {
            try {
                if (this.model.requestNo) {
                    const docs = action === 'verify-doc' ? this.waitApproveDocs : this.uploadedDocs;
                    const grouped = groupBy(docs, ['departmentCode', 'departmentType'])
                    let payloads = [] as any[];
                    Object.entries(grouped).forEach(([department, docs]) => {
                        const dpm = department.split('_');
                        payloads.push({
                            departmentCode: dpm[0],
                            departmentType: dpm[1],
                        })
                    });
                    const res = await complaintService.verifyAdditionalDocs(this.model.requestNo, payloads);
                    if (res && res.status === 200) {
                        this.displayAlertDialog({
                            success: true,
                            header: this.$t('t-save-success'),
                            messageTitle: this.$t('t-save-completed'),
                            callBack: this.goToListPage
                        } as BaseAlert)
                    }
                }
            } catch (e) {
                this.displayAlertDialog({
                    display: true,
                    success: false,
                    messageTitle: action === 'verify-doc' ? this.$t('t-action-verify-doc-fail') : this.$t('t-action-send-doc-to-dpm-fail'),
                } as BaseAlert)
            }
        },
        confirmEnd(complaint: ComplaintInfo) {
            this.model.ascEndReason = complaint.ascEndReason;
            this.displayConfirmDialog({
                messageTitle: this.$t('t-confirm-end'),
                callBack: this.endComplaint,
                callBackCancel: this.showCancelDialog
            } as BaseConfirm)
        },
        async endComplaint() {
            try {
                if (this.model.requestNo) {
                    this.displayLoading = true;
                    const res = await complaintService.ascEndRequest(this.model.requestNo, {
                        reason: this.model.ascEndReason,
                    });
                    if (res && res.status === 200) {
                        this.displayLoading = false;
                        this.displayAlertDialog({
                            success: true,
                            header: this.$t('t-save-success'),
                            messageTitle: this.$t('t-action-cancel-complaint-success'),
                            callBack: this.goToListPage
                        } as BaseAlert)
                    } else {
                        throw new Error();
                    }
                }
            } catch (e) {
                this.displayLoading = false;
                this.displayAlertDialog({
                    display: true,
                    success: false,
                    messageTitle: this.$t('t-action-cancel-complaint-fail'),
                } as BaseAlert)
            }
        },
        confirmSave() {
            this.displayConfirmDialog({
                messageTitle: this.$t('t-confirm-save'),
                callBack: this.doAscSave
            } as BaseAlert)
        },
        async uploadFileBeforeSave() {
            try {
                let allUploads = [] as AttachmentUploaderType[];
                if (this.model.ascDepartment?.fileUploads && this.model.ascDepartment?.fileUploads.length > 0) {
                    if (!this.model.ascDepartment?.attachments) this.model.ascDepartment.attachments = [];
                    allUploads = allUploads.concat([...this.model.ascDepartment?.fileUploads]);
                }

                if (allUploads && allUploads.length > 0) {
                    // prepare payloads
                    let payloads = [] as File[];
                    allUploads.forEach(attach => {
                        if (attach.blob) {
                            const blob = fileService.dataURLToBlob(attach.blob);
                            let file = new File([blob], attach.name, { type: attach.fileType })
                            payloads.push(file);
                        }
                    });
                    // call upload API
                    const res = await fileService.upload(payloads);
                    if (res && res.status === 201) {
                        const data = res.data as UploadResponse;
                        data.files.forEach(file => {
                            let attachment = {
                                itemNo: -1,
                                fileName: file.fileName,
                                runningNo: -1,
                                size: allUploads?.find(upload => upload.name === file.fileName)?.size,
                                url: file.objectName,
                            } as AttachmentUrl;
                            // add to attachment
                            if (this.model.ascDepartment?.fileUploads?.find(o => o.name === attachment.fileName) !== undefined) {
                                this.model.ascDepartment?.attachments?.push(attachment);
                            }
                        });
                        // clear attach uploads
                        if (this.model.ascDepartment) {
                            this.model.ascDepartment.fileUploads = [];
                        }
                    } else {
                        throw new Error();
                    }
                }
            } catch (e: any) {
                throw new Error("Upload Error");
            }
        },
        async doAscSave() {
            try {
                await this.uploadFileBeforeSave();
                if (this.model.requestNo) {
                    let payloads = {
                        departmentCode: this.model.ascDepartment?.departmentCode,
                        departmentType: this.model.ascDepartment?.departmentType,
                        otherRemark: this.model.ascDepartment?.otherRemark,
                        attachments: this.model.ascDepartment?.attachments,
                    }
                    const res = await complaintService.ascSave(this.model.requestNo, payloads)
                    if (res && res.status === 200) {
                        this.displayAlertDialog({
                            success: true,
                            messageTitle: this.$t('t-save-success'),
                            callBack: this.goToListPage
                        } as BaseAlert)
                    }
                }
            } catch (e: any) {
                if (e.message === 'Upload Error') {
                    this.displayAlertDialog({
                        success: false,
                        messageTitle: this.$t('t-upload-file-fail'),
                    } as BaseAlert)
                } else {
                    this.displayAlertDialog({
                        success: false,
                        messageTitle: this.$t('t-save-fail'),
                    } as BaseAlert)
                }
            }
        },
        goToListPage() {
            this.router.push({
                path: `/complaint-list`
            })
        },
        displayConfirmDialog(model: BaseConfirm) {
            this.$emit('displayConfirmDialog', model);
        },
        displayAlertDialog(model: BaseAlert) {
            this.$emit('displayAlertDialog', model);
        },
        getPdfContent() {
            return this.pdfContent;
        },
        userEndReasonText() {
            const endReason = this.userEndReasons.find(o => o.id === this.model.userEndReasonId)
            return this.lang === LANG_EN ? endReason?.reasonEn : endReason?.reasonTh;
        },
        validateBeforeCancel() {
            if (this.mainDpmSelectCoordinate) {
                this.displayConfirmDialog({
                    messageTitle: `${this.$t('t-department')} ${this.model.relatedMainDepartment?.departmentCode} ${this.$t('t-action-cancel-complaint-coordinate-confirm')}`,
                    callBack: this.showCancelDialog,
                } as BaseConfirm)
            } else {
                this.showCancelDialog();
            }
        },
        showCancelDialog() {
            this.modal.displayCancelComplaint = true
        }
    },
};
</script>

<template>
    <div class="bg-page-home main-page"></div>
    <div class="bg-page-home-content complaint-detail app-form-input">
        <Breadcrumb :title="$t('t-complaint')" v-model="filteredBreadcrumb" @view-history="modal.displayHistory = true"
            @do-need-additional-docs="modal.displayReqDoc = true" @do-send-to-department="modal.displayAssignDpm = true"
            @do-verify-additional-docs="modal.displayViewDoc = true" @do-asc-save="confirmSave"
            @do-send-docs-to-department="modal.displaySendDocToDpm = true"
            @do-notify-cancel-request="validateBeforeCancel" @do-export-pdf="$emit('exportPdf')" />
        <div ref="pdfContent" class="pdf-content">
            <RequestHeader v-if="!isNew && model" v-model="model"></RequestHeader>
            <RequestAppeal v-if="!isNew && model" v-model="model"></RequestAppeal>
            <!-- ASC end-request reason -->
            <v-card v-if="enableAscEndReason" class="app-card">
                <v-toolbar density="compact">
                    <v-toolbar-title>{{ $t('t-complaint-answer-back-info') }}</v-toolbar-title>
                </v-toolbar>
                <v-divider></v-divider>
                <v-card-text>
                    <v-textarea v-model.trim="model.ascEndReason" class="hide-float-label" variant="outlined"
                        hide-details="auto" density="comfortable" auto-grow readonly>
                    </v-textarea>
                    <div class="textarea-export formatted-new-line mt-2" style="display: none;">
                        {{ model.ascEndReason ?? '-' }}
                    </div>
                </v-card-text>
            </v-card>
            <!-- User end-request reason -->
            <v-card v-if="enableUserEndReason" class="app-card">
                <v-toolbar density="compact">
                    <v-toolbar-title>{{ $t('t-complaint-user-end-reason') }}</v-toolbar-title>
                </v-toolbar>
                <v-divider></v-divider>
                <v-card-text>
                    <v-textarea v-if="!model.userEndReasonId || model.userEndReasonId === 99"
                        v-model.trim="model.userEndReason" class="hide-float-label" variant="outlined"
                        hide-details="auto" density="comfortable" auto-grow readonly>
                    </v-textarea>
                    <v-textarea v-else :value="userEndReasonText()" class="hide-float-label" variant="outlined"
                        hide-details="auto" density="comfortable" auto-grow readonly>
                    </v-textarea>
                    <div v-if="!model.userEndReasonId || model.userEndReasonId === 99"
                        class="textarea-export formatted-new-line mt-2" style="display: none;">
                        {{ model.userEndReason ?? '-' }}
                    </div>
                    <div v-else class="textarea-export formatted-new-line mt-2" style="display: none;">
                        {{ userEndReasonText() ?? '-' }}
                    </div>
                </v-card-text>
            </v-card>
            <AscAnswer v-if="userSv.isAsc()" v-model="model.ascDepartment" :view-mode="disableAscEdit"
                @display-alert-dialog="displayAlertDialog"></AscAnswer>
            <v-alert v-if="mainDpmReturn && subDpmInprog"
                :title="'รอการดำเนินงานของฝ่ายย่อย ถึงจะสามารถดำเนินการขั้นต่อไปได้'" type="info" variant="tonal"
                class="mb-4" density="compact">
            </v-alert>
            <!-- answer & status from each department -->
            <AllAnswer v-model="model"></AllAnswer>

            <!-- all requested additional document(s) -->
            <RequestAttachment v-if="allAdditionalDocs && allAdditionalDocs.length > 0" v-model="allAdditionalDocs"
                :user-mode="false">
            </RequestAttachment>
            <!-- request information -->
            <RequestInformation v-model="model"></RequestInformation>
        </div>
        <div class="space-for-desktop" style="height: 50px;"></div>
        <div class="space-for-mobile" style="display: none"></div>

        <!-- bellow components is all MODAL -->
        <!-- ASC view histories-->
        <RequestHistory v-model="modal.displayHistory" :items="model.activitiesLogs"></RequestHistory>
        <!-- ASC request additional doc(s) by themselves -->
        <RequestAdditionalDoc v-model="modal.displayReqDoc" :complaintDepartment="model.ascDepartment"
            @on-confirm="displayConfirmDialog" @do-request-additional-docs="requestAdditionalDocs"
            @display-alert-dialog="displayAlertDialog">
        </RequestAdditionalDoc>
        <!-- ASC assign department -->
        <AssignDepartment v-model="modal.displayAssignDpm" :complaint="model" :is-re-assign="mainDpmSelectCoordinate"
            @on-confirm="assignDepartment" @display-confirm-dialog="displayConfirmDialog"
            @display-alert-dialog="displayAlertDialog">
        </AssignDepartment>
        <!-- ASC view doc(s) that was requested by department before send to user (approve/non-approve) -->
        <VerifyAdditionalDoc v-model="modal.displayViewDoc" :complaintAdditionalDocs="waitApproveDocs"
            :action="'verify-doc'" @on-confirm="verifyAdditionalDocs">
        </VerifyAdditionalDoc>
        <!-- ASC view doc(s) from user before send to Department -->
        <VerifyAdditionalDoc v-model="modal.displaySendDocToDpm" :complaintAdditionalDocs="uploadedDocs"
            :action="'send-dpm'" :view-file="true" :header="'t-action-send-doc-to-dpm'" :confirm-text="'t-confirm'"
            :cancel-text="'t-back'" @on-confirm="verifyAdditionalDocs">
        </VerifyAdditionalDoc>
        <!-- ASC cancel complaint -->
        <CancelComplaint v-model="modal.displayCancelComplaint" :complaint="model" @on-confirm="confirmEnd">
        </CancelComplaint>
        <!-- Loading -->
        <v-overlay persistent :model-value="displayLoading" class="align-center justify-center">
            <v-progress-circular color="primary" size="64" indeterminate></v-progress-circular>
        </v-overlay>
    </div>
</template>